(() => {
  'use strict';

  if (window.__choicesIosUploadFixV3) return;
  window.__choicesIosUploadFixV3 = true;

  const STANDARD_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const TYPE_BY_EXTENSION = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif'
  };

  function extension(file) {
    return String(file?.name || '').split('.').pop().toLowerCase();
  }

  function inferredType(file) {
    return String(file?.type || '').toLowerCase() || TYPE_BY_EXTENSION[extension(file)] || '';
  }

  function photoField(widget) {
    return widget?.querySelector('[data-field="photo"]') || widget?.querySelector('[data-fit-field="photo"]');
  }

  function showError(widget, message) {
    const field = photoField(widget);
    if (typeof widget?.error === 'function' && field) {
      widget.error(field, message);
      return;
    }
    if (typeof widget?.setFieldError === 'function' && field) {
      widget.setFieldError(field, message);
      return;
    }
    const node = field?.querySelector('.choices-profile__field-error, [data-error]');
    if (field) field.classList.add('has-error');
    if (node) {
      node.textContent = message;
      node.hidden = false;
    }
  }

  function clearWidgetErrors(widget) {
    widget?.querySelectorAll('.has-error').forEach((node) => node.classList.remove('has-error'));
    widget?.querySelectorAll('[data-error], .choices-profile__field-error, .choices-profile__error-summary').forEach((node) => {
      node.hidden = true;
      if (node.matches('[data-error]')) node.textContent = '';
    });
  }

  function normalizeKnownFile(file, type) {
    if (file.type === type) return file;
    return new File([file], file.name || `choices-photo.${type.split('/')[1]}`, {
      type,
      lastModified: file.lastModified || Date.now()
    });
  }

  function loadImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('This iPhone photo could not be decoded.'));
      };
      image.src = url;
    });
  }

  async function convertToJpeg(file) {
    const image = await loadImage(file);
    const maxDimension = 2400;
    const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height));
    const width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
    const height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Photo conversion is unavailable in this browser.');
    context.drawImage(image, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (!blob) throw new Error('This iPhone photo could not be converted.');
    const baseName = String(file.name || 'choices-photo').replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  }

  function prepareInputs(root = document) {
    root.querySelectorAll?.('choices-try-on input[type="file"]').forEach((input) => {
      input.accept = 'image/jpeg,image/png,image/webp,image/heic,image/heif,image/*';
      input.removeAttribute('capture');
    });
  }

  document.addEventListener('click', (event) => {
    const retry = event.target.closest?.('[data-retry], button');
    if (!retry) return;
    const widget = retry.closest?.('choices-try-on');
    const isChooseAnother = retry.matches?.('[data-retry]') || /choose another photo/i.test(retry.textContent || '');
    if (!widget || !isChooseAnother) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const input = widget.fileInput || widget.querySelector('input[type="file"]');

    try {
      if (typeof widget.reset === 'function') {
        widget.reset();
      } else {
        widget.file = null;
        widget.querySelectorAll('[data-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.panel !== 'upload';
        });
      }
    } catch (_error) {
      widget.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.panel !== 'upload';
      });
    }

    clearWidgetErrors(widget);

    if (input) {
      input.value = '';
      input.click();
    }
  }, true);

  document.addEventListener('click', (event) => {
    const input = event.target.closest?.('choices-try-on input[type="file"]');
    if (input) input.value = '';
  }, true);

  document.addEventListener('change', async (event) => {
    const input = event.target.closest?.('choices-try-on input[type="file"]');
    if (!input) return;
    const file = input.files?.[0];
    if (!file) return;

    const widget = input.closest('choices-try-on');
    const type = inferredType(file);
    if (STANDARD_TYPES.has(type) && file.type) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      let normalized;
      if (STANDARD_TYPES.has(type)) normalized = normalizeKnownFile(file, type);
      else normalized = await convertToJpeg(file);

      if (typeof widget?.handleFile !== 'function') throw new Error('The fitting-room uploader is still loading. Please try once more.');
      widget.handleFile(normalized);
    } catch (error) {
      showError(widget, error.message || 'That photo could not be uploaded. Try a screenshot or JPEG instead.');
    }
  }, true);

  document.addEventListener('choices:try-on-ready', () => prepareInputs());
  document.addEventListener('shopify:section:load', (event) => prepareInputs(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => prepareInputs(), { once: true });
  else prepareInputs();
})();
